<script setup lang="ts">
import type { VoiceFileShare } from '~/api/voiceFileShare'
import { Button, Input } from '@eci-saas/ui'
import { getVoiceFileShare, voiceFileShareSampleAudioUrl, voiceFileShareSampleDownloadUrl, voiceFileShareZipUrl } from '~/api/voiceFileShare'
import { IconDownload, IconVolume } from '~/modules/icon'

definePage({
  meta: {
    layout: false,
  },
})

const route = useRoute()
const loading = ref(true)
const error = ref('')
const share = ref<VoiceFileShare | null>(null)
const accessCode = ref('')
const submittedAccessCode = ref('')

const token = computed(() => String((route.params as Record<string, string | string[] | undefined>).token || ''))
const samples = computed(() => share.value?.samples || [])
const zipUrl = computed(() => token.value && submittedAccessCode.value ? voiceFileShareZipUrl(token.value, submittedAccessCode.value) : '')

function sampleTitle(sample: NonNullable<VoiceFileShare['samples']>[number], index: number) {
  return sample.filename || `Voice Sample ${index + 1}`
}

async function loadShare() {
  if (!token.value) {
    error.value = 'The share link is invalid.'
    loading.value = false
    return
  }
  if (!accessCode.value.trim()) {
    error.value = 'Please enter the access code.'
    loading.value = false
    return
  }

  try {
    loading.value = true
    error.value = ''
    submittedAccessCode.value = accessCode.value.trim()
    share.value = await getVoiceFileShare(token.value, submittedAccessCode.value)
  }
  catch {
    share.value = null
    submittedAccessCode.value = ''
    error.value = 'This share link is unavailable or has expired.'
  }
  finally {
    loading.value = false
  }
}

onMounted(() => {
  const code = route.query.code
  accessCode.value = Array.isArray(code) ? String(code[0] || '') : String(code || '')
  if (accessCode.value)
    loadShare()
  else
    loading.value = false
})
</script>

<template>
  <main class="min-h-screen bg-slate-50 text-slate-950">
    <div class="mx-auto flex min-h-screen w-full max-w-5xl flex-col px-5 py-8">
      <header class="mb-6 flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <p class="text-sm font-medium text-slate-500">
            ECI Cast
          </p>
          <h1 class="mt-1 text-2xl font-semibold">
            Voice Share
          </h1>
          <p v-if="share?.expiresAt" class="mt-2 text-sm text-slate-500">
            Expires {{ new Date(share.expiresAt).toLocaleString() }}
          </p>
        </div>

        <a v-if="share && samples.length > 0" :href="zipUrl" download>
          <Button>
            <IconDownload class="h-4 w-4 mr-2" />
            Download ZIP
          </Button>
        </a>
      </header>

      <section v-if="!share" class="mx-auto w-full max-w-md rounded border border-slate-200 bg-white p-6 shadow-sm">
        <h2 class="text-lg font-medium">
          Enter Access Code
        </h2>
        <p class="mt-2 text-sm text-slate-500">
          Use the code provided with this voice share link.
        </p>
        <div class="mt-4 flex gap-2">
          <Input v-model="accessCode" placeholder="Access code" @keyup.enter="loadShare" />
          <Button :disabled="loading" @click="loadShare">
            Open
          </Button>
        </div>
        <p v-if="error" class="mt-3 text-sm text-red-600">
          {{ error }}
        </p>
      </section>

      <section v-else-if="loading" class="rounded border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
        Loading voice samples...
      </section>

      <section v-else-if="error" class="rounded border border-slate-200 bg-white p-8 text-center">
        <h2 class="text-lg font-medium">
          Link unavailable
        </h2>
        <p class="mt-2 text-sm text-slate-500">
          {{ error }}
        </p>
      </section>

      <section v-else-if="samples.length === 0" class="rounded border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
        No voice samples are available in this share.
      </section>

      <section v-else class="space-y-3">
        <article
          v-for="(sample, index) in samples"
          :key="sample.id"
          class="grid gap-4 rounded border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[minmax(0,1fr)_minmax(280px,440px)] md:items-center"
        >
          <div class="min-w-0">
            <h2 class="flex items-center gap-2 truncate text-base font-medium">
              <IconVolume class="h-4 w-4 shrink-0 text-slate-500" />
              <span class="truncate">{{ sampleTitle(sample, index) }}</span>
            </h2>
          </div>

          <div class="flex min-w-0 items-center gap-2">
            <audio
              v-if="sample.id"
              controls
              preload="none"
              class="h-10 min-w-0 flex-1"
              :src="voiceFileShareSampleAudioUrl(token, submittedAccessCode, sample.id)"
            />
            <a
              v-if="sample.id"
              :href="voiceFileShareSampleDownloadUrl(token, submittedAccessCode, sample.id)"
              :download="sample.filename"
              class="inline-flex h-10 w-10 shrink-0 items-center justify-center text-slate-500 hover:text-slate-950"
              title="Download"
            >
              <IconDownload class="h-4 w-4" />
            </a>
          </div>
        </article>
      </section>
    </div>
  </main>
</template>
